import { Link } from "react-router-dom";
import { blacklistEntries } from "@/mocks/blacklist";
import type { RiskEntry } from "@/types/content";
import RiskBadge from "@/components/feature/RiskBadge";
import SectionHeading from "@/components/feature/SectionHeading";

const TOP_TAGS = ["跑路记录", "退款无门", "客服失联", "垫付返现", "高息诱惑", "预付充值", "物流停滞", "解冻费"];

export default function LatestRecords() {
  const entries = [...(blacklistEntries as RiskEntry[])]
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
    .slice(0, 5);

  const totalComplaints = (blacklistEntries as RiskEntry[]).reduce(
    (sum, entry) => sum + entry.complaintCount,
    0,
  );

  return (
    <section className="mx-auto max-w-content px-4 py-14 md:px-6 md:py-20">
      <SectionHeading
        eyebrow="最新收录"
        title="刚刚更新的风险记录"
        desc="平台情况会变化，记录也会持续更新。最近有新增反馈的条目会优先显示在这里。"
      />

      <div className="mt-9 grid grid-cols-1 gap-8 lg:grid-cols-[1.6fr_1fr]">
        <div className="flex flex-col divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
          {entries.map((entry) => (
            <Link
              key={entry.id}
              to={`/blacklist/${entry.id}`}
              className="group flex flex-col gap-3 p-5 transition-colors hover:bg-primary-50/50 cursor-pointer sm:flex-row sm:items-center"
            >
              <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-heading text-base font-bold text-foreground-950 group-hover:text-primary-700">
                    {entry.name}
                  </span>
                  <RiskBadge level={entry.riskLevel} text={entry.riskText} />
                </div>
                <span className="mt-1.5 font-body text-sm leading-relaxed text-foreground-600 line-clamp-1">
                  {entry.summary}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-5 font-label text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-chat-3-line" />
                  {entry.complaintCount}
                </span>
                <span className="hidden sm:inline-flex">{entry.updatedAt}</span>
                <i className="ri-arrow-right-s-line text-lg text-foreground-400 group-hover:text-primary-600" />
              </div>
            </Link>
          ))}
          <Link
            to="/blacklist"
            className="flex items-center justify-center gap-1.5 bg-background-100 px-5 py-4 font-label text-sm font-medium text-primary-700 transition-colors hover:bg-primary-50 cursor-pointer"
          >
            查看全部收录记录
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        <aside className="flex flex-col gap-5">
          <div className="rounded-lg border border-background-200 bg-secondary-900 p-6 text-background-50">
            <p className="font-label text-xs uppercase tracking-widest text-secondary-200">累计投诉汇总</p>
            <p className="mt-3 font-heading text-4xl font-black">{totalComplaints.toLocaleString()}</p>
            <p className="mt-2 font-label text-sm text-secondary-100">条用户反馈已被收录整理</p>
            <Link
              to="/guides/report-channels-list"
              className="mt-5 inline-flex items-center gap-1.5 font-label text-sm font-medium text-background-50 underline decoration-secondary-400 underline-offset-4 transition-colors hover:text-secondary-100 cursor-pointer"
            >
              遇到问题怎么投诉
              <i className="ri-arrow-right-up-line" />
            </Link>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <h3 className="font-heading text-base font-bold text-foreground-950">高频风险标签</h3>
            <div className="mt-4 flex flex-wrap gap-2">
              {TOP_TAGS.map((tag) => (
                <Link
                  key={tag}
                  to={`/blacklist?q=${encodeURIComponent(tag)}`}
                  className="rounded-full bg-background-100 px-3 py-1.5 font-label text-xs text-foreground-700 transition-colors hover:bg-accent-100 hover:text-accent-800 cursor-pointer"
                >
                  {tag}
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}